import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Download, Lock, ShieldCheck, Coffee, ShoppingCart, Search, Plus } from 'lucide-react';
import { UpsellPopup } from './UpsellPopup';
interface OrderBump {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  priceNum: number;
  price: string;
  image: string;
}
const BASE_PRICE = 14.99;
const BUMP_PRICE = 6.39;
// Pre-built Shopify cart permalinks keyed by sorted selected bump IDs
const CART_URL_MAP: Record<string, string> = {
  '': 'https://rutinasimple.com//cart/52146528747801:1',
  energia: 'https://rutinasimple.com//cart/52178537906457:1',
  super: 'https://rutinasimple.com//cart/52178550489369:1',
  diario: 'https://rutinasimple.com//cart/52178550620441:1',
  'energia,super': 'https://rutinasimple.com//cart/52178539512089:1',
  'energia,diario': 'https://rutinasimple.com//cart/52178607145241:1',
  'super,diario': 'https://rutinasimple.com//cart/52178597282073:1',
  'energia,super,diario': 'https://rutinasimple.com//cart/52178548457753:1'
};
const ORDER_BUMPS: OrderBump[] = [{
  id: 'energia',
  icon: <Coffee className="w-5 h-5 text-dusty-rose" data-id="element-114" />,
  title: 'EL PROTOCOLO "ADIÓS-CAFEÍNA" (ENERGÍA SIN CHOQUE)',
  description: '¿Te despiertas cansada o sientes que tu energía se desploma a las 3 p.m.? Si dependes del café para sobrevivir, estás estresando tus glándulas y provocando inflamación abdominal por exceso de cortisol. Con este protocolo de 5 páginas, aprenderás el "Hack del Aceite" para que el café no te inflame y los 3 sustitutos naturales que restauran tu energía real en 7 días.',
  priceNum: BUMP_PRICE,
  price: '$6.39 USD',
  image: 'https://cdn.magicpatterns.com/uploads/ghtdndXcR3UtrVh3Fc5MeC/order_mump-1.png'
}, {
  id: 'super',
  icon: <ShoppingCart className="w-5 h-5 text-dusty-rose" data-id="element-115" />,
  title: 'LISTA DE SÚPER: COMPRA INTELIGENTE 28D',
  description: 'Deja de tirar dinero en alimentos que se pudren en tu refrigerador o que sabotean tus hormonas. Lleva en tu celular la lista exacta de qué comprar (y qué evitar) según la fase de tu ciclo en la que te encuentres. Incluye la "Lista Negra" de los 7 ingredientes ocultos en el supermercado que disparan tu insulina y te hinchan al instante.',
  priceNum: BUMP_PRICE,
  price: '$6.39 USD',
  image: 'https://cdn.magicpatterns.com/uploads/aWPxHWRwr4rHdpZ97RpUmM/order_bump-2.png'
}, {
  id: 'diario',
  icon: <Search className="w-5 h-5 text-dusty-rose" data-id="element-116" />,
  title: 'DIARIO DE SEÑALES BIOLÓGICAS (TRACKER 2 MIN)',
  description: 'Deja de ser una extraña en tu propio cuerpo. Con este diario interactivo de 2 minutos, aprenderás a predecir tu humor, tus antojos y tu día de ovulación sin depender de apps complicadas. Identifica tu "Semáforo de Inflamación" diario para saber exactamente qué necesita tu metabolismo hoy y toma el control total de tu ciclo.',
  priceNum: BUMP_PRICE,
  price: '$6.39 USD',
  image: 'https://cdn.magicpatterns.com/uploads/jGkmDB2cMU6m8v2yumpNTU/order_bump-3.png'
}];
export function PricingSection() {
  const [selectedBumps, setSelectedBumps] = useState<Record<string, boolean>>({});
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const toggleBump = (id: string) => {
    setSelectedBumps(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const totalPrice = useMemo(() => {
    const bumpTotal = ORDER_BUMPS.filter(b => selectedBumps[b.id]).reduce((sum, b) => sum + b.priceNum, 0);
    return (BASE_PRICE + bumpTotal).toFixed(2);
  }, [selectedBumps]);
  const cartUrl = useMemo(() => {
    const key = ORDER_BUMPS.filter(b => selectedBumps[b.id]).map(b => b.id).join(',');
    return CART_URL_MAP[key] || CART_URL_MAP[''];
  }, [selectedBumps]);
  const selectedCount = Object.values(selectedBumps).filter(Boolean).length;
  const handleCheckoutClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (selectedCount === 0) {
      // Trigger upsell popup if no bumps selected
      setIsUpsellOpen(true);
    } else {
      // Proceed to checkout normally
      if (window.fbq) {
        window.fbq('track', 'AddToCart');
      }
      window.location.href = cartUrl;
    }
  };
  return <>
      <section id="pricing" className="py-12 md:py-32 bg-warm-beige relative overflow-hidden" data-id="element-117">
        {/* Decorative blobs */}
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-white/40 blur-3xl pointer-events-none" data-id="element-118" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[400px] h-[400px] rounded-full bg-dusty-rose/20 blur-3xl pointer-events-none" data-id="element-119" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-120">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-white rounded-3xl shadow-2xl p-6 md:p-12 text-center border-t-8 border-dusty-rose" data-id="element-121">
            <h2 className="text-3xl font-heading font-bold text-deep-charcoal mb-2" data-id="element-122">
              Empieza tu Transformación Hoy
            </h2>
            <p className="text-deep-charcoal/70 mb-6" data-id="element-123">
              Acceso instantáneo a todo el material
            </p>

            {/* Offer Stack inside pricing */}
            <div className="text-left space-y-3 mb-8" data-id="element-124">
              {[{
              title: 'Guía Maestra: El Código Hormonal 28D',
              value: '$97.00',
              image: 'https://cdn.magicpatterns.com/uploads/iag9TmkkPqHt9rfpmjUPTT/mk-2.png'
            }, {
              title: 'Recetario "Cocina para tus Hormonas"',
              value: '$47.00',
              image: 'https://cdn.magicpatterns.com/uploads/oKJGkmB9rrETgHjB3nYV6Z/bn-1.png'
            }, {
              title: 'Calendario Fitness "Ciclo-Sync"',
              value: '$37.00',
              image: 'https://cdn.magicpatterns.com/uploads/oKmRiA6GJEpWwEfxsfGEEY/bn-2.png'
            }, {
              title: 'Protocolos SOS Anti-Inflamación',
              value: '$27.00',
              image: 'https://cdn.magicpatterns.com/uploads/7dvccXjT6Pkv6779BcnoGy/bn-3.png'
            }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-cream-white/60 rounded-lg p-3 border border-warm-beige" data-id="element-125">
                  <img src={item.image} alt={item.title} className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover flex-shrink-0 bg-warm-beige" data-id="element-126" />
                  <div className="flex-grow min-w-0" data-id="element-127">
                    <p className="text-sm md:text-base font-semibold text-deep-charcoal leading-snug truncate" data-id="element-128">
                      {item.title}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-deep-charcoal/50 line-through flex-shrink-0" data-id="element-129">
                    {item.value}
                  </span>
                </div>)}
            </div>

            {/* Price */}
            <div className="flex flex-col items-center justify-center mb-8" data-id="element-130">
              <div className="text-lg text-deep-charcoal/60 font-medium mb-2 flex items-center gap-2" data-id="element-131">
                VALOR TOTAL:{' '}
                <span className="line-through decoration-dusty-rose decoration-2" data-id="element-132">
                  $208.00
                </span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-133">
                <span className="text-2xl font-bold text-deep-charcoal" data-id="element-134">
                  HOY SOLO:
                </span>
                <motion.span key={totalPrice} initial={{
                scale: 1.15,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20
              }} className="text-6xl md:text-7xl font-bold text-dusty-rose tracking-tight" data-id="element-135">
                  ${totalPrice}
                </motion.span>
                <span className="text-xl font-bold text-dusty-rose" data-id="element-136">USD</span>
              </div>
              <p className="text-sm text-deep-charcoal/60 mt-2" data-id="element-137">
                Pago único. Sin suscripciones. Descarga digital inmediata.
              </p>
              {selectedCount > 0 && <motion.p initial={{
              opacity: 0,
              y: -5
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-sm text-sage-green font-semibold mt-2" data-id="element-138">
                  Pack $14.99 + {selectedCount} extra
                  {selectedCount > 1 ? 's' : ''} incluido
                  {selectedCount > 1 ? 's' : ''}
                </motion.p>}
            </div>

            <a href={cartUrl} onClick={handleCheckoutClick} className="group relative w-full flex items-center justify-center px-8 py-5 text-lg md:text-xl font-bold text-white transition-all duration-300 bg-dusty-rose rounded-2xl hover:bg-dusty-rose/90 hover:shadow-xl hover:-translate-y-1 mb-6 no-underline" data-id="element-139">
              OBTENER ACCESO AHORA — ${totalPrice} USD
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-140" />
            </a>

            {/* Order Bumps */}
            <div className="space-y-3 mb-6 text-left" data-id="element-141">
              {ORDER_BUMPS.map(bump => <div key={bump.id} onClick={() => toggleBump(bump.id)} className={`relative rounded-xl border-2 transition-all duration-200 cursor-pointer overflow-hidden ${selectedBumps[bump.id] ? 'border-sage-green bg-sage-green/5 shadow-md' : 'border-dusty-rose bg-cream-white/50 bump-shimmer'}`} data-id="element-142">
                  <div className="bg-dusty-rose py-1.5 px-4 text-center" data-id="element-143">
                    <p className="text-xs font-bold text-white uppercase tracking-widest" data-id="element-144">
                      Altamente Recomendado
                    </p>
                  </div>
                  <div className="flex items-start gap-3 p-4" data-id="element-145">
                    {/* Checkbox */}
                    <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 mt-1 flex items-center justify-center transition-colors ${selectedBumps[bump.id] ? 'bg-sage-green border-sage-green' : 'border-deep-charcoal/30 bg-white'}`} data-id="element-146">
                      {selectedBumps[bump.id] && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-147">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-148" />
                        </svg>}
                    </div>

                    {/* Image */}
                    <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-warm-beige" data-id="element-149">
                      <img src={bump.image} alt={bump.title} className="w-full h-full object-cover" data-id="element-150" />
                    </div>

                    {/* Content */}
                    <div className="flex-grow min-w-0" data-id="element-151">
                      <div className="flex items-center gap-2 mb-1" data-id="element-152">
                        {bump.icon}
                        <span className="text-xs font-bold text-dusty-rose uppercase tracking-wider" data-id="element-153">
                          Añádelo por solo {bump.price}
                        </span>
                      </div>
                      <h4 className="text-sm md:text-base font-bold text-deep-charcoal leading-snug mb-1" data-id="element-154">
                        {bump.title}
                      </h4>
                      <p className="text-xs md:text-sm text-deep-charcoal/70 leading-relaxed" data-id="element-155">
                        {bump.description}
                      </p>
                    </div>
                  </div>

                  {/* Add badge */}
                  {!selectedBumps[bump.id] && <div className="absolute top-3 right-3" data-id="element-156">
                      <div className="w-6 h-6 rounded-full bg-dusty-rose/10 flex items-center justify-center" data-id="element-157">
                        <Plus className="w-3.5 h-3.5 text-dusty-rose" data-id="element-158" />
                      </div>
                    </div>}
                </div>)}
            </div>

            <p className="text-deep-charcoal font-medium mb-4" data-id="element-159">
              ¡Quiero empezar a sincronizar mi cuerpo hoy mismo!
            </p>

            <a href={cartUrl} onClick={handleCheckoutClick} className="group relative w-full flex items-center justify-center px-8 py-5 text-lg md:text-xl font-bold text-white transition-all duration-300 bg-dusty-rose rounded-2xl hover:bg-dusty-rose/90 hover:shadow-xl hover:-translate-y-1 mb-6 no-underline" data-id="element-160">
              OBTENER ACCESO AHORA — ${totalPrice} USD
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-161" />
            </a>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-sm text-deep-charcoal/60" data-id="element-162">
              <div className="flex items-center gap-2" data-id="element-163">
                <Lock className="w-4 h-4" data-id="element-164" />
                <span data-id="element-165">Pago 100% seguro</span>
              </div>
              <span className="hidden md:inline" data-id="element-166">•</span>
              <div className="flex items-center gap-2" data-id="element-167">
                <Download className="w-4 h-4" data-id="element-168" />
                <span data-id="element-169">Acceso inmediato</span>
              </div>
              <span className="hidden md:inline" data-id="element-170">•</span>
              <div className="flex items-center gap-2" data-id="element-171">
                <ShieldCheck className="w-4 h-4" data-id="element-172" />
                <span data-id="element-173">Garantía de 7 días sin preguntas</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <UpsellPopup isOpen={isUpsellOpen} onClose={() => setIsUpsellOpen(false)} data-id="element-174" />
    </>;
}